import React, { useMemo } from 'react';
import {
  Upload,
  Check,
  AlertCircle,
  Cloud,
  Sparkles,
  Loader2,
  FileImage,
  Server,
  Database,
  X
} from 'lucide-react';
import { UploadProgress } from '../../services/uploadConnector';

interface UploadProgressOverlayProps {
  progress: UploadProgress | null;
  isVisible: boolean;
  filesCount?: number;
  onSkip?: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatTime = (ms: number): string => {
  if (ms < 1000) return 'less than a second';
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
};

// Map detailed stages to display stages
const getDisplayStage = (stage: string): string => {
  switch (stage) {
    case 'preparing': return 'preparing';
    case 'uploading': return 'uploading';
    case 'server_receiving':
    case 'cloudinary_upload':
    case 'finalizing':
      return 'processing';
    case 'complete': return 'complete';
    default: return stage;
  }
};

const stages = [
  { key: 'preparing', label: 'Preparing', icon: FileImage },
  { key: 'uploading', label: 'Uploading', icon: Upload },
  { key: 'processing', label: 'Processing', icon: Cloud },
  { key: 'complete', label: 'Complete', icon: Check },
];

export function UploadProgressOverlay({
  progress,
  isVisible,
  filesCount = 0,
  onSkip
}: UploadProgressOverlayProps) {
  const displayStage = useMemo(() => {
    if (!progress) return 'preparing';
    return getDisplayStage(progress.stage);
  }, [progress]);

  const currentStageIndex = useMemo(() => {
    return stages.findIndex(s => s.key === displayStage);
  }, [displayStage]);

  const stageMessage = useMemo(() => {
    if (!progress) return 'Preparing...';

    switch (progress.stage) {
      case 'preparing':
        return `Preparing ${filesCount} images...`;
      case 'uploading':
        if (progress.bytesUploaded && progress.bytesTotal) {
          return `${formatBytes(progress.bytesUploaded)} of ${formatBytes(progress.bytesTotal)}`;
        }
        return `Uploading ${filesCount} images...`;
      case 'server_receiving':
        return 'Server receiving files...';
      case 'cloudinary_upload':
        if (progress.filesProcessed !== undefined && progress.filesTotal) {
          return `Processing ${progress.filesProcessed} of ${progress.filesTotal} images`;
        }
        return progress.message || 'Uploading to cloud storage...';
      case 'finalizing':
        return 'Finalizing upload...';
      case 'complete':
        return 'All images uploaded successfully!';
      case 'error':
        return progress.message || 'An error occurred';
      default:
        return progress.message || 'Processing...';
    }
  }, [progress, filesCount]);

  const estimatedTimeDisplay = useMemo(() => {
    if (!progress?.estimatedTimeRemaining) return null;
    if (progress.stage === 'complete' || progress.stage === 'error') return null;
    return formatTime(progress.estimatedTimeRemaining);
  }, [progress]);

  if (!isVisible) return null;

  const isInProgress = progress?.stage === 'uploading' ||
    progress?.stage === 'preparing' ||
    progress?.stage === 'server_receiving' ||
    progress?.stage === 'cloudinary_upload' ||
    progress?.stage === 'finalizing';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-md animate-in fade-in duration-300">
      <div className="relative w-full max-w-2xl mx-4 animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
        {/* Background gradient glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-purple-500/30 to-primary/30 blur-3xl opacity-60 animate-pulse" />

        {/* Main card */}
        <div className="relative bg-card border border-border rounded-3xl p-10 shadow-2xl">
          {/* Skip button - always visible during upload */}
          {onSkip && isInProgress && (
            <button
              onClick={onSkip}
              className="absolute top-6 right-6 flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Continue in background
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Animated icon */}
          <div className="flex justify-center mb-8">
            <div
              className={`w-28 h-28 rounded-full flex items-center justify-center transition-all duration-500 ${
                progress?.stage === 'complete'
                  ? 'bg-emerald-500/20'
                  : progress?.stage === 'error'
                    ? 'bg-destructive/20'
                    : 'bg-primary/20'
              }`}
            >
              {progress?.stage === 'complete' ? (
                <div className="animate-in zoom-in duration-300">
                  <Check className="w-14 h-14 text-emerald-500" />
                </div>
              ) : progress?.stage === 'error' ? (
                <AlertCircle className="w-14 h-14 text-destructive" />
              ) : progress?.stage === 'uploading' ? (
                <Upload className="w-14 h-14 text-primary animate-pulse" />
              ) : progress?.stage === 'server_receiving' ? (
                <Server className="w-14 h-14 text-primary animate-pulse" />
              ) : progress?.stage === 'cloudinary_upload' ? (
                <Cloud className="w-14 h-14 text-primary animate-bounce" />
              ) : progress?.stage === 'finalizing' ? (
                <Database className="w-14 h-14 text-primary animate-pulse" />
              ) : (
                <Loader2 className="w-14 h-14 text-primary animate-spin" />
              )}
            </div>
          </div>

          {/* Stage indicators */}
          <div className="flex justify-center items-center gap-3 mb-8">
            {stages.map((stage, index) => (
              <React.Fragment key={stage.key}>
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      index < currentStageIndex
                        ? 'bg-primary text-primary-foreground scale-100'
                        : index === currentStageIndex
                          ? 'bg-primary/20 text-primary ring-2 ring-primary ring-offset-2 ring-offset-background scale-110'
                          : 'bg-muted text-muted-foreground scale-90 opacity-50'
                    }`}
                  >
                    {index < currentStageIndex ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <stage.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs font-medium ${
                    index <= currentStageIndex ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {stage.label}
                  </span>
                </div>
                {index < stages.length - 1 && (
                  <div
                    className={`w-12 h-1 rounded-full transition-all duration-500 mb-6 ${
                      index < currentStageIndex ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Progress percentage */}
          <div className="text-center mb-6">
            <span className="text-7xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent tabular-nums">
              {progress?.percentage || 0}%
            </span>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="relative h-4 bg-muted rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary via-purple-500 to-primary rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress?.percentage || 0}%` }}
              />
              {/* Shimmer effect */}
              {progress?.stage !== 'complete' && progress?.stage !== 'error' && (
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
                  style={{
                    backgroundSize: '200% 100%',
                  }}
                />
              )}
            </div>
          </div>

          {/* Status message */}
          <p className="text-center text-muted-foreground transition-all duration-300">
            {stageMessage}
          </p>

          {/* Estimated time remaining */}
          {estimatedTimeDisplay && (
            <p className="text-center text-sm text-muted-foreground/70 mt-2">
              Estimated time remaining: <span className="font-medium tabular-nums">{estimatedTimeDisplay}</span>
            </p>
          )}

          {/* File count badge */}
          {filesCount > 0 && progress?.stage !== 'complete' && (
            <div className="flex justify-center mt-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm">
                <FileImage className="w-4 h-4 text-muted-foreground" />
                <span>{filesCount} images</span>
              </div>
            </div>
          )}

          {/* Speed indicator for uploads */}
          {progress?.stage === 'uploading' && progress.bytesUploaded && progress.bytesTotal && (
            <div className="flex justify-center mt-3">
              <div className="text-xs text-muted-foreground">
                {Math.round((progress.bytesUploaded / progress.bytesTotal) * filesCount)} of {filesCount} files
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
}
